import type { ReactNode } from "react";
import type { DrawerProps as MuiDrawerProps } from "@mui/material/Drawer";
import MuiDrawer from "@mui/material/Drawer";
import type { StyledComponentBase } from "@/components/component-base.types";

export interface DrawerProps extends StyledComponentBase {
  /** Edge from which the drawer enters. */
  anchor?: MuiDrawerProps["anchor"];
  /** Drawer contents. */
  children: ReactNode;
  /** Controls whether the drawer is visible. */
  open: boolean;
  /** Called when the drawer requests an open-state change. */
  onOpenChange: (open: boolean) => void;
  /** Class applied to the drawer paper surface. */
  paperClassName?: string;
  /** Class applied to the modal backdrop. */
  backdropClassName?: string;
  /** Test id applied to the modal backdrop. */
  backdropTestId?: string;
  /** Keeps drawer contents mounted while closed. */
  keepMounted?: boolean;
  /** Test id applied to the drawer root. */
  testId?: string;
}

/**
 * Shared VNext wrapper around MUI Drawer.
 * @param props Drawer properties.
 * @returns A temporary drawer surface.
 */
export const Drawer = (props: DrawerProps) => {
  const {
    anchor = "left",
    children,
    open,
    onOpenChange,
    className,
    paperClassName,
    backdropClassName,
    backdropTestId,
    keepMounted = false,
    testId,
  } = props;

  return (
    <MuiDrawer
      anchor={anchor}
      open={open}
      onClose={() => onOpenChange(false)}
      className={className}
      data-testid={testId}
      ModalProps={{ keepMounted }}
      slotProps={
        {
          paper: { className: paperClassName },
          backdrop: {
            className: backdropClassName,
            "data-testid": backdropTestId,
          },
        } as MuiDrawerProps["slotProps"]
      }
    >
      {children}
    </MuiDrawer>
  );
};
